import { type MouseEvent, useMemo } from "react";

import { PANEL_STEPS } from "@/components/ui/panel-stack-context";

import { RecordPanelProvider, useRecordLinkClick, useRecordPanel } from "./record-panel-context";

/**
 * The app's deal panel: ``?deal=<id>`` on any route opens the deal in the right panel, through the
 * shared record panel (``record-panel-context.tsx``); ``DealPanel`` renders it. It shows the same view
 * the standalone ``/deals/{id}`` route renders.
 */
export const DEAL_PARAM = PANEL_STEPS.deal.param;

/** The record panel's provider: it holds the deal step with the other record kinds. */
export const DealPanelProvider = RecordPanelProvider;

interface DealPanelContextType {
  /** The deal on the URL, or null. */
  dealId: string | null;
  /** `fromPanel`: a drill-in from inside the deal panel (Other deals, a linked deal) keeps the back link
   *  to the deal it came from. Without it (the board, a list) the deal opens fresh, with no back link. */
  open: (dealId: string, options?: { fromPanel?: boolean }) => void;
  close: () => void;
}

export function useDealPanel(): DealPanelContextType {
  const { id, open, close } = useRecordPanel("deal");
  return useMemo(() => ({ dealId: id, open, close }), [id, open, close]);
}

/** A plain click on a link to a deal's page opens the deal in the right panel instead (see
 *  ``useRecordLinkClick``). */
export function useDealLinkClick(): (e: MouseEvent, href: string) => void {
  return useRecordLinkClick();
}
